import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { Wordmark } from "@/components/site/Brand";
import { CLINIC, CLINIC_HOURS, SERVICES } from "@/lib/data";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import {
  Video, Building2, CalendarClock, ArrowUpRight, Phone, MapPin,
  ShieldCheck, Stethoscope, HeartPulse, CheckCircle2, Star,
} from "lucide-react";

const TYPES = [
  { id: "video", label: "Video Consultation", icon: Video, desc: "Consult Dr. Jain securely from home." },
  { id: "in-person", label: "In-Person Visit", icon: Building2, desc: "Visit our clinic in St. Inez, Goa." },
];

const TRUST = [
  { icon: Stethoscope, title: "AIIMS-trained", text: "MBBS, DNB, MD (AIIMS, New Delhi)" },
  { icon: HeartPulse, title: "Fertility expert", text: "Certified Fertility Specialist, Garbhagudi" },
  { icon: ShieldCheck, title: "Confidential care", text: "Private, judgement-free & compassionate" },
];

export default function BookConsultation() {
  const [type, setType] = useState("video");
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/appointments", { ...form, consultation_type: type });
      trackEvent("generate_lead", { event_category: "appointment", consultation_type: type, source: "book_ads" });
      setDone(true);
      toast.success("Request received! Dr. Jain's team will call you to confirm.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <main className="min-h-screen bg-cream overflow-x-hidden" data-testid="book-page">
      {/* Minimal conversion header */}
      <header className="sticky top-0 z-40 bg-cream/85 backdrop-blur-xl border-b border-blush/30">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
          <a href="/" data-testid="book-logo"><Wordmark /></a>
          <a href="tel:+919322743917" data-testid="book-call-btn"
             className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-olive-deep text-cream font-sans text-sm hover:bg-blush-dusty transition-colors duration-300">
            <Phone className="w-4 h-4" /> <span className="hidden sm:inline">{CLINIC.phone}</span><span className="sm:hidden">Call</span>
          </a>
        </div>
      </header>

      <section className="relative grain">
        <div className="absolute -top-20 -right-24 w-[34rem] h-[34rem] rounded-full bg-blush-pale/60 blur-[90px]" />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left: value + trust */}
          <div>
            <Reveal>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blush/40 bg-white/60 backdrop-blur-sm mb-6">
                <span className="w-2 h-2 rounded-full bg-blush-dusty animate-pulse" />
                <span className="font-sans text-xs tracking-[0.25em] uppercase text-olive-sage">St. Inez, Goa</span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="font-serif font-light text-olive-deep leading-[1.0] text-4xl sm:text-5xl lg:text-6xl">
                Book your consultation with <span className="italic text-blush-dusty">Dr. Shivani Jain</span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 font-sans text-base sm:text-lg text-olive-sage max-w-xl leading-relaxed">
                Expert, compassionate women's health & fertility care — available by video or in person.
                Share your details and our team will confirm your slot personally.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-8 space-y-3">
                {TRUST.map((t) => {
                  const Icon = t.icon;
                  return (
                    <div key={t.title} className="flex items-start gap-3.5">
                      <div className="w-10 h-10 rounded-2xl bg-white shrink-0 flex items-center justify-center border border-blush/30">
                        <Icon className="w-5 h-5 text-blush-dusty" />
                      </div>
                      <div>
                        <p className="font-serif text-lg text-olive-deep leading-tight">{t.title}</p>
                        <p className="font-sans text-sm text-olive-sage">{t.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Reveal>

            <Reveal delay={0.4}>
              <div className="mt-8 flex items-center gap-1.5">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-blush-dusty text-blush-dusty" />)}
                <span className="font-sans text-sm text-olive-sage ml-2">Trusted by women across Goa</span>
              </div>
            </Reveal>

            <Reveal delay={0.45}>
              <div className="mt-8 rounded-3xl border border-blush/40 bg-white/60 backdrop-blur-md p-6 max-w-md">
                <div className="flex items-center gap-2.5 mb-3">
                  <CalendarClock className="w-4 h-4 text-blush-dusty" />
                  <span className="font-sans text-xs tracking-[0.28em] uppercase text-olive-sage">Clinic Hours</span>
                </div>
                <dl className="divide-y divide-olive-sage/12">
                  {CLINIC_HOURS.map((h) => (
                    <div key={h.day} className="flex items-baseline justify-between gap-6 py-1.5">
                      <dt className="font-sans text-sm text-olive-deep shrink-0 w-24">{h.day}</dt>
                      <dd className="font-sans text-[13px] text-olive-sage text-right leading-snug">{h.time}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-4 pt-4 border-t border-olive-sage/12 flex gap-3">
                  <MapPin className="w-4 h-4 text-blush-dusty shrink-0 mt-0.5" />
                  <span className="font-sans text-sm text-olive-sage">{CLINIC.address}</span>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: booking card (sticky) */}
          <div className="lg:sticky lg:top-24">
            <Reveal delay={0.15}>
              <div className="rounded-3xl bg-olive-deep text-cream p-7 sm:p-9 shadow-[0_30px_80px_rgba(47,51,43,0.25)]">
                {done ? (
                  <div className="py-10 text-center" data-testid="book-success">
                    <CheckCircle2 className="w-14 h-14 text-blush-pale mx-auto" />
                    <h2 className="font-serif text-3xl mt-5">Thank you!</h2>
                    <p className="font-sans text-beige/85 mt-3 max-w-xs mx-auto">
                      Your {type === "video" ? "video consultation" : "in-person visit"} request is received.
                      Dr. Jain's team will call you shortly to confirm.
                    </p>
                    <a href={CLINIC.calendly} target="_blank" rel="noopener noreferrer" data-testid="book-success-calendly"
                       onClick={() => trackEvent("calendly_click", { source: "book_ads_success" })}
                       className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cream text-olive-deep font-sans text-sm hover:bg-blush-pale transition-colors">
                      Or pick a slot now on Calendly <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                ) : (
                  <>
                    <p className="font-serif text-2xl sm:text-3xl">Request your appointment</p>
                    <p className="font-sans text-sm text-beige/80 mt-2 mb-6">Free to request · confirmed by our team</p>

                    <div className="grid grid-cols-2 gap-3 mb-6">
                      {TYPES.map((t) => {
                        const Icon = t.icon;
                        const active = type === t.id;
                        return (
                          <button key={t.id} type="button" onClick={() => setType(t.id)} data-testid={`book-type-${t.id}`}
                            className={`text-left p-4 rounded-2xl border transition-colors duration-300 ${
                              active ? "bg-cream text-olive-deep border-cream" : "bg-white/5 border-white/15 hover:border-white/40"}`}>
                            <Icon className={`w-5 h-5 ${active ? "text-blush-dusty" : "text-beige"}`} />
                            <p className="font-serif text-lg mt-2 leading-tight">{t.label}</p>
                            <p className={`font-sans text-xs mt-1 leading-snug ${active ? "text-olive-sage" : "text-beige/75"}`}>{t.desc}</p>
                          </button>
                        );
                      })}
                    </div>

                    <form onSubmit={submit} data-testid="book-form" className="space-y-3.5">
                      <Field label="Full name" value={form.name} onChange={set("name")} testid="book-name" required />
                      <div className="grid sm:grid-cols-2 gap-3.5">
                        <Field label="Phone" value={form.phone} onChange={set("phone")} testid="book-phone" required />
                        <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="book-email" required />
                      </div>
                      <Field label="Preferred date" type="date" value={form.preferred_date} onChange={set("preferred_date")} testid="book-date" />
                      <div>
                        <label className="font-sans text-xs tracking-widest uppercase text-beige">Reason / notes (optional)</label>
                        <textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="book-notes"
                          className="mt-1.5 w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 font-sans text-sm text-cream placeholder-beige/50 outline-none focus:ring-2 focus:ring-blush-pale transition" />
                      </div>
                      <button type="submit" disabled={loading} data-testid="book-submit"
                        className="w-full py-4 rounded-full bg-blush-dusty text-cream font-sans text-sm hover:bg-blush-dusty/90 transition-colors duration-300 disabled:opacity-60">
                        {loading ? "Sending…" : `Request ${type === "video" ? "video" : "in-person"} appointment`}
                      </button>
                    </form>

                    <div className="flex items-center gap-3 my-5">
                      <span className="h-px flex-1 bg-white/15" /><span className="font-sans text-xs text-beige/70">or</span><span className="h-px flex-1 bg-white/15" />
                    </div>
                    <a href={CLINIC.calendly} target="_blank" rel="noopener noreferrer" data-testid="book-calendly"
                       onClick={() => trackEvent("calendly_click", { source: "book_ads" })}
                       className="group flex items-center justify-between gap-3 p-4 rounded-2xl bg-white/5 border border-white/15 hover:border-white/40 transition-colors duration-300">
                      <div className="flex items-center gap-3">
                        <CalendarClock className="w-5 h-5 text-blush-pale" />
                        <div>
                          <p className="font-serif text-lg leading-none">Self-schedule instantly</p>
                          <p className="font-sans text-xs text-beige/75 mt-1">Pick a 15-min slot on Calendly</p>
                        </div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </a>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Services strip for relevance/quality-score */}
      <section className="border-t border-blush/30 bg-blush-pale/40 py-12">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-blush-dusty mb-6">Consultations available for</p>
          <div className="flex flex-wrap gap-2.5">
            {SERVICES.map((s) => (
              <span key={s.title} className="font-sans text-sm text-olive-deep bg-white rounded-full px-4 py-2 border border-blush/30">
                {s.title}
              </span>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-olive-deep text-cream py-8">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-sans text-xs text-beige/60">© {new Date().getFullYear()} {CLINIC.full}</p>
          <a href="tel:+919322743917" className="font-sans text-sm text-beige hover:text-cream transition-colors inline-flex items-center gap-2">
            <Phone className="w-4 h-4" /> {CLINIC.phone}
          </a>
        </div>
      </footer>
    </main>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="font-sans text-xs tracking-widest uppercase text-beige">{label}</label>
      <input type={type} value={value} onChange={onChange} required={required} data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 font-sans text-sm text-cream placeholder-beige/50 outline-none focus:ring-2 focus:ring-blush-pale transition" />
    </div>
  );
}
